import { useEffect, useRef } from 'react';

const basisCards = [
  {
    label: '실리콘 시공 인건비(1일)',
    value: '24만원',
    note: '업계 평균 기준',
    icon: 'ri-user-fill',
  },
  {
    label: '1일 작업량',
    value: '약 800개',
    note: '현장 기준',
    icon: 'ri-stack-fill',
  },
  {
    label: '개당 인건비',
    value: '300원',
    note: '24만원 ÷ 800개',
    icon: 'ri-divide-fill',
    highlight: true,
  },
  {
    label: '개당 재료비',
    value: '50원',
    note: '실리콘 소모량 기준',
    icon: 'ri-test-tube-fill',
    highlight: true,
  },
];

const savingsCards = [
  {
    amount: '300원',
    label: '개당 인건비 절감',
    icon: 'ri-user-smile-fill',
    accent: '#FF6B35',
    accentBg: 'bg-[#FF6B35]/15',
    accentText: 'text-[#FF6B35]',
    accentBorder: 'border-[#FF6B35]/30',
  },
  {
    amount: '50원',
    label: '개당 재료비 절감',
    icon: 'ri-drop-fill',
    accent: '#FBBF24',
    accentBg: 'bg-yellow-400/15',
    accentText: 'text-yellow-400',
    accentBorder: 'border-yellow-400/30',
  },
  {
    amount: '350원',
    label: '개당 총 절감액',
    icon: 'ri-coin-fill',
    accent: '#34D399',
    accentBg: 'bg-emerald-400/15',
    accentText: 'text-emerald-400',
    accentBorder: 'border-emerald-400/30',
    big: true,
  },
];

const CostSavingsSection = () => {
  const titleRef = useRef<HTMLDivElement>(null);
  const basisRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    [titleRef, basisRef, resultsRef].forEach(
      (r) => r.current && observer.observe(r.current)
    );
    cardRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="cost-savings" className="py-24 bg-[#0d1120]">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">

        {/* 타이틀 */}
        <div
          ref={titleRef}
          className="text-center mb-14 opacity-0 translate-y-6 transition-all duration-700"
        >
          <span className="text-[#FF6B35] text-sm font-semibold tracking-widest uppercase">
            Cost Savings
          </span>
          <h2 className="mt-3 text-3xl lg:text-4xl font-bold text-white font-display">
            Smart Cap 도입 시{' '}
            <span className="text-[#FF6B35]">개당 350원 절감</span>
          </h2>
          <p className="mt-4 text-gray-400 text-base max-w-2xl mx-auto leading-relaxed">
            실리콘 도포작업 공정 삭제로 인건비 + 재료비 + 공사기간을 동시에 절약합니다
          </p>
          <div className="mt-4 w-12 h-1 bg-[#FF6B35] mx-auto rounded-full" />
        </div>

        {/* 산출 근거 카드 4개 가로 배치 */}
        <div
          ref={basisRef}
          className="opacity-0 translate-y-8 transition-all duration-700 delay-100 mb-4"
        >
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#FF6B35]/20">
              <i className="ri-calculator-fill text-[#FF6B35] text-sm" />
            </div>
            <span className="text-white font-bold text-sm tracking-wide">산출 근거</span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {basisCards.map((card, i) => (
              <div
                key={i}
                className={`rounded-2xl border p-5 flex flex-col gap-3 ${
                  card.highlight
                    ? 'bg-[#FF6B35]/10 border-[#FF6B35]/30'
                    : 'bg-white/5 border-white/10'
                }`}
              >
                <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${
                  card.highlight ? 'bg-[#FF6B35]/25' : 'bg-white/8'
                }`}>
                  <i className={`${card.icon} text-base ${card.highlight ? 'text-[#FF6B35]' : 'text-gray-400'}`} />
                </div>
                <div>
                  <p className="text-gray-400 text-xs leading-snug mb-1">{card.label}</p>
                  <p className={`font-black text-2xl leading-tight ${card.highlight ? 'text-[#FF6B35]' : 'text-white'}`}>
                    {card.value}
                  </p>
                  <p className="text-gray-500 text-[11px] mt-1">{card.note}</p>
                </div>
              </div>
            ))}
          </div>

          {/* 흐름 연결 화살표 */}
          <div className="flex items-center justify-center gap-2 mt-5 mb-1">
            <div className="h-px flex-1 bg-white/10" />
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5">
              <i className="ri-arrow-down-fill text-[#FF6B35] text-sm" />
              <span className="text-gray-400 text-xs font-medium">위 데이터를 기반으로 한 절감 결과</span>
            </div>
            <div className="h-px flex-1 bg-white/10" />
          </div>
        </div>

        {/* 결과 카드 3개 + 주석 */}
        <div
          ref={resultsRef}
          className="relative z-10 opacity-0 translate-y-8 transition-all duration-700 delay-200 mt-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {savingsCards.map((card, i) => (
              <div
                key={i}
                ref={(el) => { cardRefs.current[i] = el; }}
                className={`rounded-2xl border p-6 flex flex-col items-center text-center gap-3
                  opacity-0 translate-y-4 transition-all duration-500
                  ${card.accentBg} ${card.accentBorder}
                  ${card.big ? 'ring-1 ring-emerald-400/30' : ''}
                `}
                style={{ transitionDelay: `${200 + i * 80}ms` }}
              >
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl ${card.accentBg} border ${card.accentBorder}`}>
                  <i className={`${card.icon} text-xl ${card.accentText}`} />
                </div>
                <div>
                  <p className="text-gray-400 text-xs font-medium mb-1">{card.label}</p>
                  <p className={`font-black leading-none ${card.accentText} ${card.big ? 'text-4xl' : 'text-3xl'}`}>
                    {card.amount}
                  </p>
                </div>
                {card.big && (
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${card.accentBg} ${card.accentText} border ${card.accentBorder}`}>
                    인건비 + 재료비
                  </span>
                )}
              </div>
            ))}
          </div>
          <p className="mt-8 text-gray-400 text-sm text-center">
            ※ 작업환경에 따라 다를 수 있음
          </p>
        </div>

      </div>
    </section>
  );
};

export default CostSavingsSection;
